import React, {
    useState, useEffect
} from 'react';

import {
    Cctv, Calendar, ChevronLeft, ChevronRight, HardHat, Activity, CheckCircle, Scan, Sun, CloudRain, Cloud, Wind, Thermometer, Loader2, BarChart3, ShieldAlert
} from 'lucide-react';

import axios from 'axios';
import AnalyticsDashboard from './AnalyticsDashboard';
import { formatImageUrl } from '../utils/urlHelper';

import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
const ConstructionDashboard = () => {
    const [photos, setPhotos] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [weatherData, setWeatherData] = useState(null);
    const [weatherLoading, setWeatherLoading] = useState(true);

    const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';

    useEffect(() => {
        const fetchWeather = async () => {
            try {
                const lat = 19.4326;
                const lon = -99.1332;
                const res = await axios.get(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,precipitation,weather_code,wind_speed_10m&timezone=auto`);
                if (res.data && res.data.current) {
                    setWeatherData(res.data.current);
                }
            } catch (err) {
                console.error("Error al obtener clima, usando fallback:", err);
                setWeatherData({
                    temperature_2m: 23.4,
                    wind_speed_10m: 11.2,
                    precipitation: 0.0,
                    weather_code: 1 // Partly cloudy
                });
            } finally {
                setWeatherLoading(false);
            }
        };
        fetchWeather();
        const weatherInterval = setInterval(fetchWeather, 300000);
        return () => clearInterval(weatherInterval);
    }, []);

    // 1. Cargar fotos reales y telemetría de red desde API (server.js)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [photosRes, incidentsRes] = await Promise.all([
                    axios.get('http://localhost:4000/api/photos'),
                    axios.get('http://localhost:4000/api/incidents')
                ]);
                if (photosRes.data && Array.isArray(photosRes.data)) {
                    const formattedPhotos = photosRes.data.map(photo => ({
                        id: photo.id, 
                        url: formatImageUrl(photo.file_path), 
                        personCount: photo.person_count || 0, 
                        date: new Date(photo.captured_at).toLocaleString(),
                        capturedAt: photo.captured_at
                    }));
                    setPhotos(formattedPhotos);
                } if (incidentsRes.data && Array.isArray(incidentsRes.data)) {
                    setIncidents(incidentsRes.data);
                }
            } catch (error) {
                console.error("Error conectando a la API:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // Carga inicial 
        const intervalId = setInterval(fetchData, 15000);
        // Polling cada 15 segundos para el Feed "En Vivo" 
        return () => clearInterval(intervalId);
        // Cleanup del componente
    }, []);
    if (loading) {
        return (<div className="min-h-screen bg-theme-bg flex items-center justify-center text-theme-text-primary"> <div className="text-center"> <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div> <p className="text-xl font-light">Conectando con la obra...</p> </div> </div>);
    }
    // Si no hay fotos, mostramos un estado vacío amigable 
    if (photos.length === 0) {
        return (<div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] flex items-center justify-center text-theme-text-primary p-4"> <div className="max-w-md text-center p-10 rounded-3xl border border-theme-border shadow-2xl backdrop-blur-xl"> <HardHat size={
            64
        } className="mx-auto text-yellow-500 mb-6 drop-shadow-lg" /> <h2 className="text-3xl font-bold mb-3 tracking-tight">Obra en Preparación</h2> <p className="text-theme-text-secondary mb-6 leading-relaxed">Las cámaras de tu proyecto están siendo enlazadas. La telemetría comenzará a fluir en breve.</p> </div> </div>);
    }
    //Preparamos los datos para la gráfica
    const chartData = photos.slice().reverse().map(p => {
        let timeLabel = '00:00';
        try {
            const dateObj = new Date(p.capturedAt || p.date);
            if (!isNaN(dateObj.getTime())) {
                const hours = String(dateObj.getHours()).padStart(2, '0');
                const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                timeLabel = `${hours}:${minutes}`;
            } else {
                const match = p.date.match(/(\d{1,2}):(\d{2})/);
                if (match) {
                    timeLabel = `${match[1].padStart(2, '0')}:${match[2]}`;
                }
            }
        } catch (e) {
            console.error("Error formatting time for chart:", e);
        }
        return {
            hora: timeLabel,
            personas: p.personCount
        };
    });
    // Calcular Métricas de Seguridad para la cuadrícula de KPIs en la parte superior
    const totalIncidents = incidents.length;
    const resolvedIncidents = incidents.filter(i => i.is_resolved).length;
    const activeIncidents = totalIncidents - resolvedIncidents;
    const safetyCompliance = totalIncidents > 0 
      ? Math.round(((resolvedIncidents) / totalIncidents) * 100) 
      : 100;

    //EL RETURN ES MUY IMPORTANTE 
    return (
        <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans selection:bg-blue-500/30">
            {/* Header */}
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2">
                        Visor de Operaciones <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Live</span>
                    </h1>
                    <p className="text-theme-text-secondary flex items-center gap-2 font-medium">
                        <Cctv size={18} className="text-blue-400" /> Cámara Frontal 01 <span className="text-slate-600 px-2">•</span> {activeProjectName}
                    </p>
                </div>

                <div className=" backdrop-blur-md px-5 py-2.5 rounded-2xl border border-theme-border flex items-center gap-3 shadow-lg hover: transition-colors">
                    <div className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </div>
                    <span className="text-sm font-bold text-theme-text-primary tracking-wide">Sistema Enlazado</span>
                </div>
            </div>

            {/* Grid de KPIs en la parte superior */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 font-sans">
                {/* KPI: Compliance Rate */}
                <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Índice EHS</span>
                        <div className="bg-emerald-500/10 p-2 rounded-xl border border-emerald-500/20 text-emerald-400">
                            <CheckCircle size={20} />
                        </div>
                    </div>
                    <div>
                        <h3 className="text-4xl font-black text-emerald-400">{safetyCompliance}%</h3>
                        <p className="text-xs text-theme-text-secondary mt-1">Cumplimiento acumulado de alertas</p>
                    </div>
                </div>

                {/* KPI: Active Incidents */}
                <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Alertas Activas</span>
                        <div className="bg-red-500/10 p-2 rounded-xl border border-red-500/20 text-red-400">
                            <ShieldAlert size={20} />
                        </div>
                    </div>
                    <div>
                        <h3 className="text-4xl font-black text-red-400">{activeIncidents}</h3>
                        <p className="text-xs text-theme-text-secondary mt-1">Acciones correctivas requeridas</p>
                    </div>
                </div>

                {/* KPI: Resolved Incidents */}
                <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Resueltos</span>
                        <div className="bg-blue-500/10 p-2 rounded-xl border border-blue-500/20 text-blue-400">
                            <CheckCircle size={20} />
                        </div>
                    </div>
                    <div>
                        <h3 className="text-4xl font-black text-blue-400">{resolvedIncidents}</h3>
                        <p className="text-xs text-theme-text-secondary mt-1">Infracciones mitigadas exitosamente</p>
                    </div>
                </div>

                {/* KPI: Total Telemetry Logs */}
                <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Total Capturas</span>
                        <div className="bg-purple-500/10 p-2 rounded-xl border border-purple-500/20 text-purple-400">
                            <Calendar size={20} />
                        </div>
                    </div>
                    <div>
                        <h3 className="text-4xl font-black text-purple-400">{photos.length}</h3>
                        <p className="text-xs text-theme-text-secondary mt-1">Fotografías y telemetry checks registrados</p>
                    </div>
                </div>
            </div>

            {/* CONTENEDOR DE VISUALIZADOR PRINCIPAL Y FEED DE IA */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* CAROUSEL DE FOTOS (Ocupa 2/3) */}
                <div className="lg:col-span-2 bg-theme-panel rounded-3xl overflow-hidden shadow-2xl border border-theme-border relative group ">
                    <img src={photos[currentIndex].url} alt="Avance de obra" className="w-full h-[400px] md:h-[650px] object-cover transition-all duration-700 ease-in-out group-hover:scale-[1.02]" />

                    {/* Overlay Gradient for readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c]/80 via-transparent to-[#0a0a0c]/40 pointer-events-none"></div>

                    {/* IA Badge: Indica cuántas personas detectó el sistema */}
                    <div className="absolute top-6 left-6 flex flex-wrap gap-3">
                        <div className="bg-blue-600/90 backdrop-blur-md px-4 py-2 rounded-xl border border-blue-400/50 flex items-center gap-2 shadow-lg shadow-blue-900/50">
                            <div className="h-2 w-2 rounded-full animate-ping"></div>
                            <span className="text-xs font-black text-theme-text-primary uppercase tracking-widest">Vision AI</span>
                        </div>

                        <div className={`px-4 py-2 rounded-xl border backdrop-blur-md shadow-lg flex items-center gap-2 transition-colors ${photos[currentIndex].personCount > 0 ? 'bg-green-500/20 border-green-500/50 text-green-300' : ' border-theme-border text-theme-text-primary'}`}>
                            <HardHat size={18} className={photos[currentIndex].personCount > 0 ? "text-green-400" : "text-theme-text-secondary"} />
                            <span className="font-bold text-sm tracking-wide"> {photos[currentIndex].personCount} Operarios Detectados </span>
                        </div>
                    </div>

                    {/* Date Overlay */}
                    <div className="absolute bottom-6 right-6 bg-theme-bg/50 backdrop-blur-xl px-5 py-3 rounded-2xl border border-theme-border flex items-center gap-3 shadow-xl">
                        <Calendar size={20} className="text-blue-400" />
                        <span className="font-mono text-lg font-bold tracking-wider text-theme-text-primary"> {photos[currentIndex].date} </span>
                    </div>

                    {/* Navigation Arrows */}
                    <button onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))} className="absolute left-6 top-1/2 -translate-y-1/2 bg-theme-bg/40 hover:bg-theme-bg/70 border border-theme-border p-3 rounded-2xl backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95" >
                        <ChevronLeft size={32} className="text-theme-text-primary" />
                    </button>
                    <button onClick={() => setCurrentIndex(prev => Math.min(photos.length - 1, prev + 1))} className="absolute right-6 top-1/2 -translate-y-1/2 bg-theme-bg/40 hover:bg-theme-bg/70 border border-theme-border p-3 rounded-2xl backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95" >
                        <ChevronRight size={32} className="text-theme-text-primary" />
                    </button>

                    {/* Timeline Control Indicator */}
                    <div className="absolute bottom-6 left-6 right-80 hidden md:flex items-center gap-4 bg-theme-bg/50 backdrop-blur-md px-5 py-3 rounded-2xl border border-theme-border shadow-xl opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-xs font-bold text-slate-600 text-theme-text-secondary uppercase tracking-wider">Línea de Vida</span>
                        <input type="range" min="0" max={photos.length - 1} value={currentIndex} onChange={(e) => setCurrentIndex(parseInt(e.target.value))} className="flex-1 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500 hover:accent-blue-400 transition-all" />
                    </div>
                </div>

                {/* RIGHT PANEL COLUMN (Ocupa 1/3) */}
                <div className="lg:col-span-1 flex flex-col gap-5 h-full max-h-[650px]">
                    {/* WEATHER ADVISOR WIDGET */}
                    <div className="bg-theme-panel/80 rounded-3xl border border-theme-border backdrop-blur-xl p-5 shadow-2xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xs font-bold flex items-center gap-2 uppercase tracking-wider text-theme-text-secondary">
                                <Sun className="text-amber-500 animate-pulse" size={16} /> Clima en Obra
                            </h3>
                            <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md border border-emerald-500/20">
                                Monitoreo Local
                            </span>
                        </div>

                        {weatherLoading || !weatherData ? (
                            <div className="flex items-center gap-3 py-2 text-theme-text-secondary text-sm">
                                <Loader2 className="animate-spin text-blue-500" size={16} />
                                <span>Cargando pronóstico...</span>
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        {weatherData.weather_code >= 51 ? (
                                            <CloudRain className="text-blue-400" size={32} />
                                        ) : weatherData.weather_code >= 1 ? (
                                            <Cloud className="text-slate-400" size={32} />
                                        ) : (
                                            <Sun className="text-amber-500" size={32} />
                                        )}
                                        <div>
                                            <span className="text-3xl font-black tracking-tight">{weatherData.temperature_2m}°C</span>
                                            <p className="text-[10px] font-semibold text-theme-text-secondary uppercase">
                                                {weatherData.weather_code >= 51 ? 'Lluvia' : weatherData.weather_code >= 1 ? 'Parcialmente Nublado' : 'Despejado'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right text-xs text-theme-text-secondary space-y-1">
                                        <p className="flex items-center justify-end gap-1"><Wind size={12} className="text-slate-400" /> {weatherData.wind_speed_10m} km/h</p>
                                        <p className="flex items-center justify-end gap-1"><CloudRain size={12} className="text-blue-400" /> Prec. {weatherData.precipitation || 0}mm</p>
                                    </div>
                                </div>

                                <div className="border-t border-theme-border pt-3">
                                    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1.5">Advisor de Retrasos</h4>
                                    {weatherData.wind_speed_10m > 30 ? (
                                        <p className="text-[11px] bg-red-500/10 border border-red-500/20 text-red-400 p-2.5 rounded-xl font-medium leading-normal">
                                            ⚠️ <strong>Alerta de Viento:</strong> Vientos de {weatherData.wind_speed_10m} km/h. Peligro en grúas torre y alturas.
                                        </p>
                                    ) : weatherData.precipitation > 2.0 || weatherData.weather_code >= 51 ? (
                                        <p className="text-[11px] bg-red-500/10 border border-red-500/20 text-red-400 p-2.5 rounded-xl font-medium leading-normal">
                                            🌧️ <strong>Alerta de Lluvia:</strong> Lluvia activa. Se desaconsejan colados de concreto.
                                        </p>
                                    ) : weatherData.temperature_2m > 32 ? (
                                        <p className="text-[11px] bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 p-2.5 rounded-xl font-medium leading-normal">
                                            🔥 <strong>Alerta de Calor:</strong> Temperatura de {weatherData.temperature_2m}°C. Asegurar hidratación constante.
                                        </p>
                                    ) : (
                                        <p className="text-[11px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-2.5 rounded-xl font-medium leading-normal">
                                            ✅ <strong>Condiciones óptimas:</strong> Clima favorable para el avance general de la obra.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* FEED DE INCIDENTES IA */}
                    <div className="bg-theme-panel/80 rounded-3xl border border-theme-border backdrop-blur-xl flex flex-col shadow-2xl overflow-hidden flex-1 max-h-[360px]">
                        <div className="p-5 border-b border-theme-border bg-gradient-to-b to-transparent flex justify-between items-center z-10">
                            <h3 className="text-sm font-bold flex items-center gap-3 tracking-tight text-theme-text-primary">
                                <Scan className="text-blue-500" size={18} /> Riesgos Activos
                            </h3>
                            <span className="bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5">
                                <div className="h-1.5 w-1.5 bg-red-400 rounded-full animate-pulse"></div> SOS
                            </span>
                        </div>

                        <div className="p-2 overflow-y-auto flex-1 custom-scrollbar">
                            {incidents.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-theme-text-secondary py-8">
                                    <CheckCircle size={36} className="mb-2 text-green-500/50" />
                                    <p className="text-xs font-medium text-theme-text-secondary">Todo en orden protocolar</p>
                                </div>
                            ) : (
                                <div className="space-y-2 p-2">
                                    {incidents.map((incident) => (
                                        <div key={incident.id} className="border border-theme-border p-3 rounded-2xl hover:bg-theme-border/20 transition-all group relative overflow-hidden">
                                            <div className="flex justify-between items-start mb-2 relative z-10">
                                                <h4 className="font-bold text-xs text-theme-text-primary">{incident.incident_type}</h4>
                                                <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest border ${incident.is_resolved ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                                                    {incident.is_resolved ? 'Controlado' : 'Alerta'}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3 text-[10px] font-medium text-theme-text-secondary relative z-10">
                                                <p className="flex items-center gap-1 px-1.5 py-0.5 bg-theme-bg/40 rounded">
                                                    <Cctv size={10} className="text-theme-text-secondary" /> Cam {incident.camera_id}
                                                </p>
                                                <p className="flex items-center gap-1 px-1.5 py-0.5 bg-theme-bg/40 rounded">
                                                    <Activity size={10} className="text-blue-500" /> {Math.round((incident.confidence_score || 0) * 100)}%
                                                </p>
                                            </div>

                                            <p className="text-[8px] text-theme-text-secondary mt-2 border-t border-theme-border pt-1.5 font-mono flex justify-between relative z-10">
                                                <span>Log Entry</span>
                                                <span>{new Date(incident.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN DE ANALÍTICAS DE IA INLINE */}
            <div className="max-w-7xl mx-auto mt-12 border-t border-theme-border/60 pt-10">
                <div className="flex items-center gap-3 mb-8">
                    <BarChart3 className="text-emerald-500 drop-shadow-md" size={32} />
                    <h2 className="text-2xl font-extrabold tracking-tight text-theme-text-primary">
                        Analíticas de Obra <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">AI</span>
                    </h2>
                </div>
                <AnalyticsDashboard inline={true} />
            </div>
        </div>);
};
export default ConstructionDashboard;

